import { Component } from '@theme/component';

/**
 * @typedef {object} SmartRecomApi
 * @property {() => boolean} isReady
 * @property {() => void} openModal
 */

const READY_CHECK_INTERVAL = 250;
const READY_CHECK_TIMEOUT = 10000;

/**
 * Opens the SmartSize fit quiz from our own button.
 * The button stays hidden until the SmartSize app embed is loaded and has a quiz for the product:
 * SmartSize always injects its own trigger next to the size guide link, and only shows it when a quiz matches the product.
 */
export class SmartsizeTrigger extends Component {
  /** @type {number | undefined} */
  #readyInterval;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleClick);

    const startedAt = Date.now();
    this.#readyInterval = window.setInterval(() => {
      if (this.#api) {
        this.hidden = false;
        window.clearInterval(this.#readyInterval);
      } else if (Date.now() - startedAt > READY_CHECK_TIMEOUT) {
        window.clearInterval(this.#readyInterval);
      }
    }, READY_CHECK_INTERVAL);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleClick);
    window.clearInterval(this.#readyInterval);
  }

  /** @returns {SmartRecomApi | null} */
  get #api() {
    const api = /** @type {SmartRecomApi | undefined} */ (/** @type {any} */ (window).SmartRecom);
    if (!api?.isReady()) return null;

    // SmartSize toggles this inline style itself; its container is hidden by our CSS, so read the attribute, not the computed style.
    const appTrigger = document.getElementById('smartrecom-inline-button-injected');
    if (!appTrigger || appTrigger.style.display === 'none') return null;

    return api;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const button = /** @type {Element | null} */ (event.target)?.closest('button');
    const api = this.#api;
    if (!button || !api) return;

    // The size guide drawer is a modal dialog in the top layer: close it first or the quiz opens behind it.
    const drawerTrigger = /** @type {(HTMLElement & {closeDialog?: (opts?: {immediate?: boolean}) => void}) | null} */ (
      this.closest('drawer-trigger')
    );
    drawerTrigger?.closeDialog?.({ immediate: true });

    api.openModal();
  };
}

customElements.define('smartsize-trigger', SmartsizeTrigger);
